import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { SEOHead } from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPinned, Info, ExternalLink, Loader2, HardHat } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ZonesMap, fmtArea, STATUS_COLORS, zoneToJobLink } from '../components/zones/ZonesMap';
import { ZoneFilters } from '../components/zones/ZoneFilters';
import { ZoneAlertsPanel } from '../components/zones/ZoneAlertsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuildingZonesPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ province: '', months: 3, status: '', commune: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await axios.get(`${API}/building-zones`, { params, withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters, user]);

  const defaulted = useRef(false);
  useEffect(() => {
    if (data?.access && !defaulted.current) {
      defaulted.current = true;
      setFilters((f) => ({ ...f, months: 12 }));
    }
  }, [data?.access]);

  const onChange = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const zones = useMemo(() => data?.zones || [], [data]);
  const access = !!data?.access;
  const canPost = access && ['company', 'both'].includes(user?.user_type);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SEOHead title="Zones à construire – Lotissements et permis d'urbanisation en Wallonie" description="Carte des nouveaux lotissements et permis d'urbanisation autorisés en Wallonie. Prospection commerciale pour entreprises de construction : province de Luxembourg, Namur, Liège, Hainaut, Brabant wallon." path="/zones-a-construire" />
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-overline text-blue-200 mb-2">Wallonie · Données ouvertes SPW</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl mb-3" data-testid="zones-title">Zones à construire</h1>
          <p className="text-blue-100 max-w-2xl text-base md:text-lg">Les lotissements et permis d'urbanisation récemment autorisés : repérez les futurs chantiers avant tout le monde et prospectez les bons endroits.</p>
          <div className="flex flex-wrap gap-2 mt-4 text-xs">
            <Badge className="bg-white/15 text-white border-0" data-testid="zones-access-badge">{access ? 'Accès Premium : 12 mois d\'historique' : 'Aperçu gratuit : 3 derniers mois'}</Badge>
            {data?.data_until && <Badge className="bg-white/15 text-white border-0">Données jusqu'au {new Date(data.data_until).toLocaleDateString('fr-BE')}</Badge>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start gap-2 text-sm text-[#4B5563] bg-white border border-[#1E3A8A]/10 rounded-lg p-3 mb-6" data-testid="zones-info-banner">
          <Info className="w-4 h-4 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
          <p>Outil de veille commerciale : ces zones ne sont pas des appels d'offres. Seules les informations administratives du projet sont affichées (aucune donnée personnelle du demandeur). Mise à jour mensuelle.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="lg:w-80 flex-shrink-0 space-y-4">
            <ZoneFilters access={access} loggedIn={!!user} filters={filters} onChange={onChange} provinces={data?.provinces || []} statuses={data?.statuses || []} total={zones.length} />
            {access && <ZoneAlertsPanel prefs={data?.zone_alerts} provinces={data?.provinces || []} filters={filters} />}
          </aside>

          <main className="flex-1 space-y-6 min-w-0">
            <Card className="overflow-hidden">
              <div className="h-[520px] relative" data-testid="zones-map-container">
                {loading && <div className="absolute inset-0 z-[500] bg-white/60 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>}
                <ZonesMap zones={zones} access={access} canPost={canPost} />
              </div>
              <div className="px-4 py-2 flex flex-wrap items-center gap-4 text-xs text-[#6B7280] border-t">
                {Object.entries(STATUS_COLORS).map(([k, c]) => (
                  <span key={k} className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: c }} />{(data?.statuses || []).find((s) => s.id === k)?.label || k}</span>
                ))}
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block bg-[#10B981]" />Autre</span>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="zones-list">
              {zones.map((z) => (
                <Card key={z.code} className="card-surface" data-testid={`zone-card-${z.code}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-heading font-bold text-[#111827] flex items-center gap-1"><MapPinned className="w-4 h-4 text-[#1E3A8A]" /> {z.commune}</p>
                        <p className="text-xs text-[#6B7280]">{z.province}</p>
                      </div>
                      <Badge style={{ background: `${STATUS_COLORS[z.status] || '#10B981'}20`, color: STATUS_COLORS[z.status] || '#10B981' }} className="border-0 text-xs">{z.status_label}</Badge>
                    </div>
                    {access ? (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[#374151]">
                        <div><p className="text-[#9CA3AF]">Superficie</p><p className="font-semibold">{fmtArea(z.area_m2)}</p></div>
                        <div><p className="text-[#9CA3AF]">Autorisation</p><p className="font-semibold">{z.decision_date ? new Date(z.decision_date).toLocaleDateString('fr-BE') : '—'}</p></div>
                        {z.document_url && <a href={z.document_url} target="_blank" rel="noopener noreferrer" className="col-span-2 inline-flex items-center gap-1 text-[#1E3A8A] font-medium"><ExternalLink className="w-3 h-3" /> Document officiel SPW</a>}
                        {canPost && <Link to={zoneToJobLink(z)} className="col-span-2 inline-flex items-center justify-center gap-1 bg-[#F59E0B]/10 text-[#B45309] border border-[#F59E0B]/30 rounded-md px-2 py-1.5 font-semibold hover:bg-[#F59E0B]/20" data-testid={`zone-card-post-job-${z.code}`}><HardHat className="w-3.5 h-3.5" /> Publier un chantier ici</Link>}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-[#9CA3AF] italic">Détails (superficie, date, document) réservés aux abonnés Premium.</p>
                    )}
                  </CardContent>
                </Card>
              ))}
              {!loading && zones.length === 0 && <Card className="p-8 text-center text-[#6B7280] md:col-span-2">Aucune zone pour ces critères.</Card>}
            </div>

            <p className="text-xs text-[#9CA3AF]" data-testid="zones-source">
              Source : <a href="https://geoportail.wallonie.be/catalogue/077c806b-449c-42a1-9522-e7748044f013.html" target="_blank" rel="noopener noreferrer" className="underline">Service public de Wallonie (SPW) – Géoportail de la Wallonie</a>, « Permis d'urbanisation et lotissements », licence CC-BY 4.0. Données informatives sans valeur légale{data?.last_sync ? ` · synchronisées le ${new Date(data.last_sync).toLocaleDateString('fr-BE')}` : ''}.
            </p>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BuildingZonesPage;
